import React, { Component } from "react";
import * as Yup from "yup";
import _ from "lodash";
import { Field } from "formik";

import {
    bulletsSelectorContainer,
    buttonSelectorContainer,
    checkboxContainer,
    dateContainer,
    formSetContainer,
    formSetWaysContainer,
    gameSelectorContainer,
    inputContainer,
    numberContainer,
    numbersArrayContainer,
    selectContainer,
    switchContainer,
    textareaContainer,
    twoPanelSelectorContainer,
    betJackpotArrayContainer,
    inputAliasContainer,
    groupContainer,
    betFreqPrizesArrayContainer,
    inputSearchContainer
} from "./formContainers";
import { SubFormDefinition } from "./FormSet";

export type FieldType =
    | "input"
    | "number"
    | "select"
    | "checkbox"
    | "buttonSelector"
    | "twoPanelSelector"
    | "textarea"
    | "formSet"
    | "formSetWays"
    | "bulletsSelector"
    | "switch"
    | "date"
    | "numbersArray"
    | "betJackpotArray"
    | "inputAlias"
    | "group"
    | "betFreqPrizesArray"
    | "gameSelector";

export interface InputFieldOptions {
    id: string;
    placeholder: string;
    inputType: string;
    label?: string;
    disabled?: boolean;
    decimal?: boolean;
    validationSchema?: any;
    isConfig?: boolean;
}

export interface NumberArrayFieldOptions {
    id: string;
    placeholder: string;
    inputType: string;
    label?: string;
    disabled?: boolean;
    decimal?: boolean;
    validationSchema?: any;
    isConfig?: boolean;
}

export interface InputAliasFieldOptions {
    id: string;
    placeholder: string;
    inputType: string;
    label?: string;
    disabled?: boolean;
    decimal?: boolean;
    validationSchema?: any;
    items?: any;
    isConfig?: boolean;
}

export interface BetJackpotArrayFieldOptions {
    id: string;
    placeholder: string;
    inputType: string;
    label?: string;
    disabled?: boolean;
    decimal?: boolean;
    validationSchema?: any;
    items?: any;
    isConfig?: boolean;
}

export interface BetFreqPrizesArrayFieldOptions {
    id: string;
    placeholder: string;
    inputType: string;
    label?: string;
    disabled?: boolean;
    decimal?: boolean;
    validationSchema?: any;
    items?: any;
    isConfig?: boolean;
}

export interface DateFieldOptions {
    id: string;
    placeholder: string;
    inputType: string;
    label?: string;
    disabled?: boolean;
    validationSchema?: any;
    showtime?: boolean;
    minutesinterval?: number;
    isConfig?: boolean;
}

export interface TextareaFieldOptions {
    id: string;
    placeholder: string;
    label?: string;
    value?: string;
    disabled?: boolean;
    classNames?: string;
    validationSchema?: any;
    isConfig?: boolean;
}

export interface SelectFieldOptions {
    id: string;
    noValueLabel: string;
    label: string;
    items: { label: any; value: any }[];
    multipleSelection?: boolean;
    sourceChanges?: boolean;
    validationSchema?: any;
    disabled?: boolean;
    isConfig?: boolean;
    headerContainer?: any;
}

export interface SwitchFieldOptions {
    id: string;
    label: string;
    validationSchema?: any;
    disabled?: boolean;
}

export interface CheckboxFieldOptions {
    id: string;
    text: string;
    validationSchema?: any;
    disabled?: boolean;
}

export interface TwoPanelSelectorOptions {
    id: string;
    items: string;
    label?: string;
    completeList?: any[];
    validationSchema?: any;
    disabled?: boolean;
}

export interface BulletsSelectOptions {
    id: string;
    label?: string;
    items?: { label: any; value: any }[];
    enableSearch?: boolean;
    orderByLabel?: boolean;
    completeList?: { label: any; value: any }[];
    validationSchema?: any;
    exclusive?: boolean;
    disabled?: boolean;
}

export interface FormSetOptions {
    id: string;
    subFormTypes: SubFormDefinition[];
    formSetLabel?: string;
    initialValues: any[];
    disabled?: boolean;
    extraFields?: any;
    subsidiary?: any;
    isConfig?: boolean;
}

export interface FormSetWaysOptions {
    id: string;
    subFormTypes: SubFormDefinition[];
    formSetLabel?: string;
    initialValues: any[];
    disabled?: boolean;
    presets?: any[];
    extraFields?: any;
    isConfig?: boolean;
    gameInstances: any;
}

export interface ButtonSelectorFieldOptions {
    id: string;
    label?: string;
    items: { label: any; selected?: boolean; value: any;[key: string]: any }[];
    columnClass?: string;
    selectedClass?: string;
    exclusive?: boolean;
    notSelectedClass?: string;
    validationSchema?: any;
    disabled?: boolean;
}

export interface GameSelectorFieldOptions {
    id: string;
    label?: string;
    selectedGames?: (string | number)[];
    completeList?: { label: any; value: any }[];
    validationSchema?: any;
    bundlesList?: { label: any; value: any; games: any[] }[];
    exclusive?: boolean;
    disabled?: boolean;
}

export interface GroupFieldOptions {
    id: string;
    label?: string;
    disabled?: boolean;
}

export interface InputSearchFieldOptions {
    id: string;
    label?: string;
    searchAction: any;
    items: any;
    selectedFilters: any;
    disabled?: boolean;
    validationSchema?: any;
}

export type FieldOptions =
    | InputFieldOptions
    | SelectFieldOptions
    | CheckboxFieldOptions
    | ButtonSelectorFieldOptions
    | TextareaFieldOptions
    | BulletsSelectOptions
    | GameSelectorFieldOptions
    | SwitchFieldOptions
    | FormSetOptions
    | FormSetWaysOptions
    | DateFieldOptions
    | NumberArrayFieldOptions
    | BetJackpotArrayFieldOptions
    | InputAliasFieldOptions
    | GroupFieldOptions
    | BetFreqPrizesArrayFieldOptions
    | InputSearchFieldOptions;

export interface FormGridField {
    name: string;
    fieldType: FieldType;
    fieldOptions: FieldOptions;
    validation?: Yup.Schema<any>;
    validationSchema?: any;
    columnClass: string;
    formSpacing?: boolean;
    component?: Component;
    key?: string;
}

export interface FormGridProps {
    fields: FormGridField[];
    isConfig?: boolean;
}

export interface FormGridState {
    fields: FormGridField[];
}

const generateComponent = (field, isConfig) => {
    if (
        field.fieldType == "number" &&
        field.validationSchema &&
        field.validationSchema.hasOwnProperty("type") &&
        field.validationSchema.type !== "integer"
    ) {
        field.fieldOptions.decimal = true;
    }
    const enhancedComponents = {
        input: inputContainer,
        number: numberContainer,
        select: selectContainer,
        buttonSelector: buttonSelectorContainer,
        checkbox: checkboxContainer,
        twoPanelSelector: twoPanelSelectorContainer,
        textarea: textareaContainer,
        formSet: formSetContainer,
        formSetWays: formSetWaysContainer,
        switch: switchContainer,
        bulletsSelector: bulletsSelectorContainer,
        gameSelector: gameSelectorContainer,
        date: dateContainer,
        numbersArray: numbersArrayContainer,
        betJackpotArray: betJackpotArrayContainer,
        inputAlias: inputAliasContainer,
        group: groupContainer,
        betFreqPrizesArray:betFreqPrizesArrayContainer,
        inputSearch: inputSearchContainer
    };

    return enhancedComponents[field.fieldType]({
        ...field.fieldOptions,
        validationSchema: field.validationSchema,
        isConfig: isConfig
    });
};

const updateFields = (fields, prev, keyFind, isConfig) => {
    return fields.map(field => {
        if (field.name == keyFind) {
            return {
                ...field,
                key: _.uniqueId(),
                component: generateComponent(field, isConfig)
            };
        } else {
            const oldField = _.find(prev.fields, ["name", field.name]);
            return (
                oldField || {
                    ...field,
                    key: _.uniqueId(),
                    component: generateComponent(field, isConfig)
                }
            );
        }
    });
};

class FormGrid extends Component<FormGridProps, FormGridState> {
    constructor(props) {
        super(props);

        this.state = {
            fields: props.fields.map(field => ({
                ...field,
                key: _.uniqueId(),
                component: generateComponent(field, this.props.isConfig),
            }))
        };
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        let fieldKey = undefined;
        let newFields = [...prevState.fields];
        let shouldUpdate = false;

        const prevFieldsDisabled = nextProps.fields.map(
            field => field.fieldOptions.disabled
        );

        const currentFieldsDisabled = prevState.fields.map(
            field => field.fieldOptions.disabled
        );

        const prevFieldsItems = nextProps.fields.map(field => {
            if (field.fieldType == "select" || field.fieldType == "bulletsSelector") {
                return field.fieldOptions.items;
            }
        });

        const currentFieldsItems = prevState.fields.map(field => {
            if (field.fieldType == "select" || field.fieldType == "bulletsSelector") {
                return field.fieldOptions.items;
            }
        });

        if (
            (!_.isEqual(prevFieldsDisabled, currentFieldsDisabled) ||
                !_.isEqual(prevFieldsItems, currentFieldsItems)) &&
            !_.isEqual(newFields.map(field => field.name), ['name', 'subsidiary', 'type', 'stype'])
        ) {
            shouldUpdate = true;
            newFields = shouldUpdate
                ? updateFields(nextProps.fields, prevState.fields, "", nextProps.isConfig)
                : newFields;
        }


        if (
            _.some(nextProps.fields, function (field) {
                if (field.name === "game_instances") {
                    fieldKey = "game_instances";
                    return true;
                }

                if (field.name === "game_groups") {
                    fieldKey = "game_groups";
                    return true;
                }

                if (field.name === "terminal_groups") {
                    fieldKey = "terminal_groups";
                    return true;
                }

                if (field.name === "stype") {
                    fieldKey = "stype";
                    return true;
                }
            })
        ) {
            const prevGameInstancesField = _.find(prevState.fields, {
                name: fieldKey
            });

            const nextGameInstancesField = _.find(nextProps.fields, {
                name: fieldKey
            });

            if (prevGameInstancesField && nextGameInstancesField) {
                const areFieldsDifferent = !_.some(nextProps.fields, field => {
                    if (field.name === "game_instances") {
                        fieldKey = "game_instances";
                        return true;
                    }
                })
                    ? !_.isEqual(
                        prevGameInstancesField.fieldOptions.items.sort(),
                        nextGameInstancesField.fieldOptions.items.sort()
                    )
                    : !_.isEqual(
                        prevGameInstancesField.fieldOptions.selectedGames.sort(),
                        nextGameInstancesField.fieldOptions.selectedGames.sort()
                    );

                if (
                    areFieldsDifferent ||
                    (prevGameInstancesField.fieldOptions.bundlesList !==
                        undefined &&
                        nextGameInstancesField.fieldOptions.bundlesList !==
                        undefined &&
                        !_.isEqual(
                            prevGameInstancesField.fieldOptions.bundlesList.sort(),
                            nextGameInstancesField.fieldOptions.bundlesList.sort()
                        ))
                ) {
                    newFields = nextProps.fields.map(field => {
                        return {
                            ...field,
                            key: _.uniqueId(),
                            component: generateComponent(field, nextProps.isConfig)
                        };
                    });

                    shouldUpdate = true;
                }
            }
        }

        let nextChildrenFieldIndex = _.findIndex(nextProps.fields, [
            "name",
            "children"
        ]);
        let prevChildrenFieldIndex = _.findIndex(prevState.fields, [
            "name",
            "children"
        ]);
        if (nextChildrenFieldIndex > -1 && prevChildrenFieldIndex > -1) {
            const prevChildrenFields =
                prevState.fields[prevChildrenFieldIndex].fieldOptions
                    .subFormTypes[0].fields;
            const nextChildrenFields =
                nextProps.fields[nextChildrenFieldIndex].fieldOptions
                    .subFormTypes[0].fields;

            const prevGroups = _.find(prevChildrenFields, field => {
                return (
                    field.name == "terminal_groups" ||
                    field.name == "game_groups"
                );
            });
            const nextGroups = _.find(nextChildrenFields, field => {
                return (
                    field.name == "terminal_groups" ||
                    field.name == "game_groups"
                );
            });

            if (prevGroups && nextGroups) {
                const areFieldsDifferent = !_.isEqual(
                    prevGroups.fieldOptions.items.sort(),
                    nextGroups.fieldOptions.items.sort()
                );

                if (areFieldsDifferent) {
                    let useProps = shouldUpdate ? newFields : nextProps.fields;
                    newFields = updateFields(useProps, prevState, "children", nextProps.isConfig);
                    shouldUpdate = true;
                }
            }
        }

        let usePropsFields = shouldUpdate ? newFields : nextProps.fields;
        const keysPrev = prevState.fields.map(objt => objt.name);
        const keysNext = usePropsFields.map(objt => objt.name);

        if (!_.isEqual(keysPrev, keysNext)) {
            newFields = updateFields(usePropsFields, prevState, "prizes", nextProps.isConfig);
            shouldUpdate = true;
        } else if (nextProps.fields.find(objt => objt.name == "prizes")) {
            const nextKeyPrizes = usePropsFields.find(
                objt => objt.name == "prizes"
            ).fieldOptions["subFormTypes"][0].fields;
            const prevKeyPrizes = prevState.fields.find(
                objt => objt.name == "prizes"
            ).fieldOptions["subFormTypes"][0].fields;

            if (!_.isEqual(prevKeyPrizes, nextKeyPrizes)) {
                newFields = updateFields(usePropsFields, prevState, "prizes", nextProps.isConfig);
                shouldUpdate = true;
            }
        }

        if (shouldUpdate) {
            return {
                ...prevState,
                fields: newFields
            };
        } else {
            return prevState;
        }
    }

    renderFields() {
        return this.state.fields.map(field => {
            return (
                <div className={field.columnClass} key={field.key}>
                    {field.formSpacing && <div className="form-spacer" />}
                    <Field
                        name={field.name}
                        component={field.component}
                        disabled={
                            field.fieldOptions.disabled !== undefined &&
                            field.fieldOptions.disabled
                        }
                    />
                </div>
            );
        });
    }

    render() {
        return <div className="row">{this.renderFields()}</div>;
    }
}

export default FormGrid;
